(() => {
  if (customElements.get('newsletter-popup')) return;
  // Domain endings reserved for documentation and testing; they never receive mail.
  const RESERVED_TLD = /\.(example|test|invalid|localhost)$/;
  // Gmail only issues names of 6-30 letters, digits and dots, so any other name cannot exist.
  const GMAIL_NAME = /^[a-z0-9.]{6,30}$/;
  const TYPO_DOMAINS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gnail.com': 'gmail.com',
    'gmaill.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmail.om': 'gmail.com', 'gmail.in': 'gmail.com',
    'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yhoo.com': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'yahoo.co': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'hotmail.con': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outloo.com': 'outlook.com', 'outlook.con': 'outlook.com',
    'redifmail.com': 'rediffmail.com', 'rediffmai.com': 'rediffmail.com', 'rediffmail.con': 'rediffmail.com',
    'icould.com': 'icloud.com', 'iclould.com': 'icloud.com', 'icloud.con': 'icloud.com',
  };
  // Letters walked along one keyboard row. The top row needs 5 because "erty" and "wert" occur in real names.
  const KEY_WALKS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'].flatMap((row, index) => {
    const size = index ? 4 : 5;
    return [row, [...row].reverse().join('')].flatMap((keys) => Array.from({ length: keys.length - size + 1 }, (_, at) => keys.slice(at, at + size)));
  }).concat('qwer');
  // Real names and words stop short of seven consonants in a row (y counts as a vowel).
  const CONSONANT_RUN = /[bcdfghjklmnpqrstvwxz]{7,}/;
  const list = (text) => (text || '').toLowerCase().split(/[\s,;]+/).filter(Boolean);
  const RETURN_WINDOW = 30 * 60 * 1000;
  const formKey = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), (n) => (n % 36).toString(36)).join('');
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.controller = new AbortController();
      this.key = `newsletter-popup:${this.dataset.sectionId}`;
      this.media = matchMedia('(max-width: 749px)');
      this.shown = false;
      this.blockedDomains = list(this.dataset.blockedDomains);
      // Matches a name built only from blocked names: test, testtest, test.user, abcxyz.
      const names = list(this.dataset.blockedNames).map((name) => name.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&'));
      const blockedName = `(?:${names.join('|')})`;
      this.blockedNames = names.length ? new RegExp(`^${blockedName}(?:[._-]?${blockedName})*$`) : null;
      this.mailDomains = new Map();
      this.inboxes = new Map();
      this.listen(this.querySelector('[data-close]'), 'click', () => this.close());
      this.listen(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      this.listen(this.dialog, 'click', (event) => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.close();
      });
      // Delegated: a server error response swaps the fields for fresh ones.
      this.listen(this, 'click', (event) => {
        if (event.target.closest('[data-copy]')) this.copy();
        const social = event.target.closest('[data-social]');
        if (social) this.beginSocial(event, social);
      });
      this.listen(this, 'input', (event) => {
        if (event.target.matches('.np-email')) this.validateEmail();
      });
      // Capture phase: Shopify's injected form-persister binds a bubbling submit
      // listener that forces a native (full page) submit unless it is stopped first.
      this.listen(this.querySelector('form'), 'submit', (event) => this.submit(event), { capture: true });
      this.listen(document, 'shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.open(true);
      });
      this.listen(document, 'shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
      });
      this.listen(this.media, 'change', () => {
        if (!this.deviceAllowed() && this.dialog.open && !window.Shopify?.designMode) this.close(false);
        else if (this.dataset.trigger === 'scroll') this.checkScroll();
        else if (this.triggerReady) this.open();
      });
      if (window.Shopify?.designMode) {
        if (this.dataset.preview === 'true') this.open(true);
        return;
      }
      if (this.finishSocial()) return;
      const result = this.querySelector('[data-result]')?.dataset.result;
      const pending = Number(this.storage('sessionStorage', 'pending'));
      const ownReturn = (pending > Date.now() - RETURN_WINDOW) || (location.hash === `#${this.querySelector('form').id}` && !this.storage('sessionStorage', 'return-consumed'));
      if (ownReturn && (result === 'success' || result === 'error')) {
        this.storage('sessionStorage', 'pending', null);
        this.storage('sessionStorage', 'return-consumed', 'true');
        this.cleanReturnUrl();
        if (result === 'success') this.storage('localStorage', 'subscribed', 'true');
        // A submitted form must return feedback even if visitor/frequency rules changed.
        if (this.dataset.enabled === 'true') this.open(true);
        return;
      }
      // A result this popup did not ask for belongs to another customer form on the page.
      if (result !== 'idle' || !this.eligible()) return;
      if (this.dataset.trigger === 'delay') {
        this.timer = setTimeout(() => { this.triggerReady = true; this.open(); }, Number(this.dataset.delay) * 1000);
      } else if (this.dataset.trigger === 'scroll') {
        this.listen(window, 'scroll', () => this.checkScroll(), { passive: true });
        this.checkScroll();
      } else if (this.dataset.trigger === 'exit') {
        // Exit intent is a desktop mouse gesture; touch devices use the configured delay.
        this.listen(document, 'mouseout', (event) => {
          if (!this.media.matches && !event.relatedTarget && event.clientY <= 0) this.open();
        });
        if (this.media.matches || matchMedia('(pointer: coarse)').matches) {
          this.timer = setTimeout(() => { this.triggerReady = true; this.open(); }, Number(this.dataset.delay) * 1000);
        }
      }
    }
    listen(target, name, callback, options = {}) {
      target?.addEventListener(name, callback, { ...options, signal: this.controller.signal });
    }
    storage(type, suffix, value) {
      try {
        const key = `${this.key}:${suffix}`;
        if (value === null) window[type].removeItem(key);
        else if (value !== undefined) window[type].setItem(key, value);
        else return window[type].getItem(key);
      } catch { /* Storage may be blocked. Keep the popup usable for this page view. */ }
      return null;
    }
    deviceAllowed() {
      return this.dataset.device === 'both' || this.dataset.device === (this.media.matches ? 'mobile' : 'desktop');
    }
    eligible() {
      if (this.dataset.enabled !== 'true' || this.dataset.pageEnabled !== 'true') return false;
      const subscribed = this.dataset.subscribed === 'true' || this.storage('localStorage', 'subscribed') === 'true';
      const visitor = subscribed ? 'subscribers' : this.dataset.loggedIn === 'true' ? 'customers' : 'guests';
      if (this.dataset[visitor] !== 'true') return false;
      const frequency = this.dataset.frequency;
      if (frequency === 'session' && this.storage('sessionStorage', 'seen')) return false;
      if (frequency === 'once' && this.storage('localStorage', 'seen')) return false;
      if (frequency === 'closed' && this.storage('localStorage', 'closed')) return false;
      const last = Number(this.storage('localStorage', 'seen'));
      if (frequency === 'days' && last && Date.now() - last < Number(this.dataset.days) * 86400000) return false;
      return true;
    }
    checkScroll() {
      const available = document.documentElement.scrollHeight - window.innerHeight;
      const percent = available > 0 ? window.scrollY / available * 100 : 0;
      if (percent >= Number(this.dataset.scroll)) this.open();
    }
    open(force = false) {
      if (this.dialog.open || (!force && (this.shown || !this.eligible() || !this.deviceAllowed()))) return;
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
      this.shown = true;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      if (!window.Shopify?.designMode) {
        this.storage('localStorage', 'seen', String(Date.now()));
        this.storage('sessionStorage', 'seen', 'true');
      }
    }
    close(remember = true) {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (remember && !window.Shopify?.designMode) this.storage('localStorage', 'closed', 'true');
      if (this.previousFocus?.isConnected) this.previousFocus.focus();
    }
    cleanReturnUrl() {
      const url = new URL(location.href);
      const form = this.querySelector('form');
      // Only remove parameters belonging to this popup's native return.
      if (url.hash !== '#' + form.id) return;
      url.searchParams.delete('customer_posted');
      if (url.searchParams.get('form_type') === 'customer') url.searchParams.delete('form_type');
      url.hash = '';
      history.replaceState(history.state, '', url.pathname + url.search);
    }
    // The input's own type/pattern/required rules cover the address format; this adds the domain rules.
    validateEmail() {
      const input = this.querySelector('.np-email');
      if (!input) return;
      input.setCustomValidity('');
      const value = input.value.trim().toLowerCase();
      if (!value) return;
      const [name, domain = ''] = value.split('@');
      if (TYPO_DOMAINS[domain]) input.setCustomValidity(`Did you mean ${name}@${TYPO_DOMAINS[domain]}?`);
      else if (!input.validity.valid || this.looksFake(name, domain)) input.setCustomValidity(this.emailError);
    }
    // Catches addresses that cannot be real. Whether a well-formed mailbox exists is only known by mailing it.
    looksFake(name, domain) {
      const mailbox = name.split('+')[0];
      const letters = mailbox.replace(/[^a-z]/g, '');
      return this.blockedDomain(domain)
        || RESERVED_TLD.test(domain)
        || (/^(gmail|googlemail)\.com$/.test(domain) && !GMAIL_NAME.test(mailbox))
        || this.blockedNames?.test(mailbox.replace(/\d+/g, '').replace(/^[._-]+|[._-]+$/g, ''))
        || /^(.)\1+$/.test(mailbox)
        || CONSONANT_RUN.test(mailbox)
        || (letters.length >= 6 && !/[aeiouy]/.test(letters))
        || KEY_WALKS.some((walk) => letters.includes(walk));
    }
    blockedDomain(host) {
      return this.blockedDomains.some((entry) => host === entry || host.endsWith('.' + entry));
    }
    get emailError() {
      return this.dataset.emailError || 'Please enter a valid email address.';
    }
    // Asks public DNS whether the domain has a mail server; only the domain is sent, never the address.
    // Throwaway-mail services rotate domains but keep their mail servers, so the server names are checked too.
    async acceptsMail(domain) {
      if (this.mailDomains.has(domain)) return this.mailDomains.get(domain);
      try {
        const response = await fetch(`https://dns.google/resolve?type=MX&name=${encodeURIComponent(domain)}`, { signal: AbortSignal.timeout(2500) });
        const { Status, Answer = [] } = await response.json();
        // 3 = no such domain, 0 = answered. Anything else is a DNS fault, not a verdict.
        if (Status !== 0 && Status !== 3) return true;
        const servers = Answer.filter((record) => record.type === 15).map((record) => record.data.split(' ').pop().replace(/\.$/, '').toLowerCase());
        const accepts = Status === 0 && servers.some(Boolean) && !servers.some((server) => this.blockedDomain(server));
        this.mailDomains.set(domain, accepts);
        return accepts;
      } catch {
        // DNS unreachable: let Shopify decide rather than turn a real visitor away.
        return true;
      }
    }
    // Asks QuickEmailVerification whether the inbox exists and returns the message to show, or '' to let it through.
    // No credits left, a timeout or an outage all count as "cannot tell": the popup then relies on its own checks.
    async inboxError(email) {
      const key = this.dataset.verifyKey;
      if (!key) return '';
      if (this.inboxes.has(email)) return this.inboxes.get(email);
      try {
        const response = await fetch(`https://api.quickemailverification.com/c/v1/verify?pub_key=${encodeURIComponent(key)}&email=${encodeURIComponent(email)}`, { signal: AbortSignal.timeout(5000) });
        if (!response.ok) return '';
        const verdict = await response.json();
        let error = '';
        if (verdict.did_you_mean) error = `Did you mean ${verdict.did_you_mean}?`;
        else if (verdict.result === 'invalid' || String(verdict.disposable) === 'true') error = this.emailError;
        this.inboxes.set(email, error);
        return error;
      } catch {
        return '';
      }
    }
    async submit(event) {
      const form = event.currentTarget;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (window.Shopify?.designMode || form.hasAttribute('aria-busy')) return;
      this.validateEmail();
      if (!form.reportValidity()) return;
      this.setBusy(form, true);
      const email = form.querySelector('.np-email');
      const address = email.value.trim().toLowerCase();
      // The free DNS check goes first so a dead domain does not spend a verification credit.
      const error = (await this.acceptsMail(address.split('@')[1])) ? await this.inboxError(address) : this.emailError;
      if (error) {
        this.setBusy(form, false);
        email.setCustomValidity(error);
        form.reportValidity();
        return;
      }
      try {
        const body = new FormData(form);
        // /contact answers 400 without a form_key.
        if (!body.get('form_key')) body.set('form_key', formKey());
        const response = await fetch(form.action, { method: 'POST', body, headers: { Accept: 'text/html' } });
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = page.getElementById(form.id)?.querySelector('[data-result]');
        // No verdict means Shopify answered with its bot check, which only a real navigation can show.
        if (!result || result.dataset.result === 'idle') {
          this.submitNatively(form);
          return;
        }
        this.querySelector('[data-result]').replaceWith(result);
        if (result.dataset.result === 'success') {
          this.storage('localStorage', 'subscribed', 'true');
          result.querySelector('.np-success')?.focus();
        } else {
          result.querySelector('.np-email')?.focus();
        }
      } catch {
        this.submitNatively(form);
      } finally {
        this.setBusy(form, false);
      }
    }
    setBusy(form, busy) {
      form.toggleAttribute('aria-busy', busy);
      const button = form.querySelector('[type="submit"]');
      if (button) button.disabled = busy;
    }
    submitNatively(form) {
      // Lets connectedCallback recognise the page it comes back to as this popup's own return.
      this.storage('sessionStorage', 'return-consumed', null);
      this.storage('sessionStorage', 'pending', String(Date.now()));
      form.submit();
    }
    beginSocial(event, link) {
      const consent = this.querySelector('.np-consent input');
      if (window.Shopify?.designMode || (consent && !consent.reportValidity())) {
        event.preventDefault();
        return;
      }
      const url = new URL(link.href);
      url.searchParams.set('return_to', location.pathname + location.search);
      link.href = url.href;
      this.storage('sessionStorage', 'social', String(Date.now()));
    }
    // The template only renders for a signed-in customer, so finding it means the sign-in completed.
    finishSocial() {
      const started = Number(this.storage('sessionStorage', 'social'));
      if (!started) return false;
      this.storage('sessionStorage', 'social', null);
      const success = this.querySelector('[data-social-success]');
      const result = this.querySelector('[data-result]');
      if (!success || !result || Date.now() - started > RETURN_WINDOW) return false;
      result.dataset.result = 'success';
      result.replaceChildren(success.content.cloneNode(true));
      this.storage('localStorage', 'subscribed', 'true');
      if (this.dataset.enabled === 'true') this.open(true);
      return true;
    }
    async copy() {
      const button = this.querySelector('[data-copy]');
      const status = this.querySelector('[data-copy-status]');
      try {
        await navigator.clipboard.writeText(button.dataset.code);
        status.textContent = 'Coupon copied!';
      } catch {
        const input = document.createElement('textarea');
        input.value = button.dataset.code;
        input.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        this.dialog.append(input);
        input.select();
        let copied = false;
        try { copied = document.execCommand('copy'); } catch { /* Show manual copy hint. */ }
        input.remove();
        button.focus();
        status.textContent = copied ? 'Coupon copied!' : `Copy this code: ${button.dataset.code}`;
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.close(false);
      this.controller?.abort();
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);
})();
